import { apiFetch, parseApiError } from 'app/javascript/services/api';
import {
  AdminCompaniesPage,
  AdminFacialUploadBonusResponse,
  AdminCompanyHealth,
  AdminCompanyMember,
  AdminFailedPhoto,
  AdminListParams,
  AdminOverview,
  AdminTrainingPhoto,
  AdminUsersPage,
} from 'app/javascript/types/admin';

const basePath = '/api/v1/admin';

function listQuery(params?: AdminListParams) {
  const searchParams = new URLSearchParams();
  if (params?.search) searchParams.set('search', params.search);
  if (params?.page) searchParams.set('page', String(params.page));
  return searchParams;
}

export const adminService = {
  async overview(): Promise<AdminOverview> {
    const response = await apiFetch(`${basePath}/overview`);
    if (!response.ok) throw new Error('Erro ao carregar o overview');
    return response.json();
  },

  async createSidekiqSession(): Promise<{ url: string }> {
    const response = await apiFetch(`${basePath}/sidekiq_session`, { method: 'POST' });
    if (!response.ok) throw new Error('Erro ao autorizar o acesso ao Sidekiq');
    return response.json();
  },

  async users(params?: AdminListParams): Promise<AdminUsersPage> {
    const response = await apiFetch(`${basePath}/users?${listQuery(params)}`);
    if (!response.ok) throw new Error('Erro ao carregar os usuários');
    return response.json();
  },

  async companies(params?: AdminListParams): Promise<AdminCompaniesPage> {
    const response = await apiFetch(`${basePath}/companies?${listQuery(params)}`);
    if (!response.ok) throw new Error('Erro ao carregar as academias');
    return response.json();
  },

  async companyHealth(slug: string): Promise<AdminCompanyHealth> {
    const response = await apiFetch(`${basePath}/companies/${slug}`);
    if (!response.ok) throw new Error('Erro ao carregar a saúde da academia');
    return response.json();
  },

  async companyMembers(slug: string): Promise<AdminCompanyMember[]> {
    const response = await apiFetch(`${basePath}/companies/${slug}/members`);
    if (!response.ok) throw new Error('Erro ao carregar os membros da academia');
    return response.json();
  },

  async companyTrainingPhotos(slug: string): Promise<AdminTrainingPhoto[]> {
    const response = await apiFetch(`${basePath}/companies/${slug}/training_photos`);
    if (!response.ok) throw new Error('Erro ao carregar as fotos dos treinos');
    return response.json();
  },

  // Both grant actions answer with the refreshed health snapshot, so the
  // page can replace what it already holds instead of refetching.
  async grantPlan(slug: string, platformPlanId: number): Promise<AdminCompanyHealth> {
    const response = await apiFetch(`${basePath}/companies/${slug}/plan_grant`, {
      method: 'POST',
      body: JSON.stringify({ platform_plan_id: platformPlanId }),
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao conceder o plano'));
    }
    return response.json();
  },

  async revokeGrant(slug: string): Promise<AdminCompanyHealth> {
    const response = await apiFetch(`${basePath}/companies/${slug}/plan_grant`, { method: 'DELETE' });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao revogar a concessão'));
    }
    return response.json();
  },

  async grantFacialUploadBonus(
    slug: string,
    idempotencyKey: string,
  ): Promise<AdminFacialUploadBonusResponse> {
    const response = await apiFetch(`${basePath}/companies/${slug}/facial_upload_bonus`, {
      method: 'POST',
      headers: { 'Idempotency-Key': idempotencyKey },
    });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao conceder o bônus de uploads'));
    }
    return response.json();
  },

  async failedPhotos(): Promise<AdminFailedPhoto[]> {
    const response = await apiFetch(`${basePath}/failed_photos`);
    if (!response.ok) throw new Error('Erro ao carregar as fotos que precisam de atenção');
    return response.json();
  },

  async reprocessFailedPhoto(id: number): Promise<void> {
    const response = await apiFetch(`${basePath}/failed_photos/${id}/reprocess`, { method: 'POST' });
    if (!response.ok) {
      throw new Error(await parseApiError(response, 'Erro ao reprocessar a foto'));
    }
  },
};
